import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { insertTextAtSelection } from '../html-placeholders';
import { useHtmlTemplateEditor } from './editor-api';

export interface HtmlSourceEditorProps {
    value: string;
    onChange(_value: string): void;
    title?: React.ReactNode;
    minHeightClassName?: string;
}

export const HtmlSourceEditor = (props: HtmlSourceEditorProps) => {
    const lineNumberRef = useRef<HTMLPreElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const intl = useIntl();
    const { registerInsertHandler } = useHtmlTemplateEditor();
    const [cursor, setCursor] = useState({ line: 1, column: 1 });
    const valueRef = useRef(props.value);
    const onChangeRef = useRef(props.onChange);
    valueRef.current = props.value;
    onChangeRef.current = props.onChange;
    const lineNumbers = Array.from({ length: Math.max(props.value.split('\n').length, 1) }, (_, index) => index + 1).join('\n');
    const minHeightClassName = props.minHeightClassName ?? 'min-h-96';

    const updateCursor = (target: HTMLTextAreaElement) => {
        const beforeCursor = target.value.slice(0, target.selectionStart);
        const lines = beforeCursor.split('\n');
        setCursor({
            line: lines.length,
            column: lines[lines.length - 1].length + 1
        });
    };

    const insertAtSelection = useCallback((text: string) => {
        const target = textareaRef.current;
        if (!target) return;

        const { next, cursor: nextCursor } = insertTextAtSelection(valueRef.current, text, target.selectionStart, target.selectionEnd);
        onChangeRef.current(next);
        requestAnimationFrame(() => {
            target.selectionStart = nextCursor;
            target.selectionEnd = nextCursor;
            target.focus();
            updateCursor(target);
        });
    }, []);

    useEffect(() => {
        registerInsertHandler(insertAtSelection);
        return () => registerInsertHandler(null);
    }, [insertAtSelection, registerInsertHandler]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== 'Tab') return;

        event.preventDefault();
        const target = event.currentTarget;
        const start = target.selectionStart;
        const end = target.selectionEnd;

        if (event.shiftKey) {
            const lineStart = props.value.lastIndexOf('\n', start - 1) + 1;
            const lineEnd = props.value.indexOf('\n', end);
            const selectionEnd = lineEnd === -1 ? props.value.length : lineEnd;
            const selectedBlock = props.value.slice(lineStart, selectionEnd);
            const nextBlock = selectedBlock.replace(/^ {1,4}/gm, '');
            const removedBeforeStart = selectedBlock.length - nextBlock.length;
            props.onChange(`${props.value.slice(0, lineStart)}${nextBlock}${props.value.slice(selectionEnd)}`);
            requestAnimationFrame(() => {
                target.selectionStart = Math.max(lineStart, start - removedBeforeStart);
                target.selectionEnd = Math.max(target.selectionStart, end - removedBeforeStart);
                updateCursor(target);
            });
            return;
        }

        const nextValue = `${props.value.slice(0, start)}    ${props.value.slice(end)}`;
        props.onChange(nextValue);
        requestAnimationFrame(() => {
            target.selectionStart = start + 4;
            target.selectionEnd = start + 4;
            updateCursor(target);
        });
    };

    const insertDroppedText = (event: React.DragEvent<HTMLTextAreaElement>) => {
        event.preventDefault();
        const text = event.dataTransfer.getData('text/plain') || event.dataTransfer.getData('text/html');
        if (!text) return;
        insertAtSelection(text);
    };

    return (
        <div className='border-border-default mx-4 flex min-h-0 flex-col self-stretch overflow-hidden rounded-md border'>
            <div className='border-border-default bg-background-default text-brand-muted flex items-center justify-between border-b px-3 py-2 text-xs font-medium'>
                {props.title ?? (
                    <FormattedMessage
                        id='content-detail.editor.html-source'
                        description='Header label above the notification content HTML source editor'
                        defaultMessage='HTML source'
                    />
                )}
                <span>
                    {intl.formatMessage(
                        {
                            id: 'content-detail.editor.cursor-position',
                            description: 'Cursor position label in the notification content HTML source editor',
                            defaultMessage: 'Ln {line}, Col {column}'
                        },
                        cursor
                    )}
                </span>
            </div>
            <div className={`flex ${minHeightClassName}`}>
                <pre
                    ref={lineNumberRef}
                    className={`bg-background-default text-brand-muted m-0 min-w-12 overflow-hidden px-3 py-3 text-right font-mono text-xs leading-6 select-none ${minHeightClassName}`}>
                    {lineNumbers}
                </pre>
                <textarea
                    ref={textareaRef}
                    className={`text-foreground-default min-w-0 flex-1 resize-y border-0 bg-white p-3 font-mono text-sm leading-6 outline-none ${minHeightClassName}`}
                    value={props.value}
                    spellCheck={false}
                    autoCapitalize='off'
                    autoCorrect='off'
                    onChange={(event) => {
                        props.onChange(event.target.value);
                        updateCursor(event.target);
                    }}
                    onClick={(event) => {
                        registerInsertHandler(insertAtSelection);
                        updateCursor(event.currentTarget);
                    }}
                    onFocus={() => registerInsertHandler(insertAtSelection)}
                    onKeyUp={(event) => updateCursor(event.currentTarget)}
                    onKeyDown={handleKeyDown}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={insertDroppedText}
                    onScroll={(event) => {
                        if (lineNumberRef.current) {
                            lineNumberRef.current.scrollTop = event.currentTarget.scrollTop;
                        }
                    }}
                />
            </div>
        </div>
    );
};
